import { InputField } from '@/components/form/InputField';
import { CenteredLayout } from '@/components/layouts/CenteredLayout';
import { Form } from '@/components/primitives/Form';
import { useTranslation } from '@/i18n';
import { TransText } from '@/i18n/trans/text';
import { breakpoints } from '@/utils/breakpoints';
import { cn } from '@/utils/cn';
import {
  createSignupMethodSchema,
  type SignupMethodValues,
} from '@/utils/zodSchema';
import { Button, Card, Typography } from '@fleet/webshop-ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { type FC } from 'react';
import { useForm } from 'react-hook-form';
import { useMediaQuery } from 'react-responsive';
import { useNavigate } from 'react-router-dom';

interface SignupEmailProps {
  onSubmit: (v: SignupMethodValues) => void;
}

const SignupEmail: FC<SignupEmailProps> = ({ onSubmit }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isLaptopOrBigger = useMediaQuery({
    minWidth: breakpoints.laptop,
  });

  const form = useForm<SignupMethodValues>({
    mode: 'onBlur',
    resolver: zodResolver(createSignupMethodSchema(t)),
    defaultValues: {
      email: '',
    },
  });

  return (
    <CenteredLayout>
      <div className="mx-auto flex w-full max-w-[410px] flex-col gap-8 p-4 tablet:my-auto laptop:p-0">
        <Typography variant={isLaptopOrBigger ? 'title' : 'mobile-title'}>
          <TransText i18nKey="createAnAccount" />
        </Typography>

        <Card className="rounded-xl">
          <Form
            form={form}
            onSubmit={form.handleSubmit(onSubmit)}
            className={cn('flex flex-col gap-6 [&_input]:h-12', {
              '[&_input]:h-8': isLaptopOrBigger,
            })}
          >
            <InputField
              name="email"
              label="Email"
              placeholder={t('text.enterYourEmailAddress')}
              type="email"
              autoCapitalize="none"
              autoComplete="email"
              autoCorrect="off"
              required
            />
            <Button
              type="submit"
              data-testid="continue-with-email"
              variant="primary"
              size="medium"
            >
              <TransText i18nKey="continueWithEmail" />
            </Button>
          </Form>
        </Card>

        <div className="flex items-center gap-3 self-center">
          <Typography variant="body1">
            <TransText i18nKey="alreadyHaveAnAccount" />
          </Typography>
          <Button
            variant="ghost"
            className={cn('w-fit p-0', 'text-primary')}
            onClick={() => navigate('/login')}
            data-testid="signup-to-login"
          >
            <TransText i18nKey="logIn" />
          </Button>
        </div>
      </div>
    </CenteredLayout>
  );
};

export default SignupEmail;
